import { useEffect, useRef, useState } from 'react';
import {
  TCmsButtonStyle,
  ISliderAdditionalButton,
  TVerticalPosition,
  THorizontalPosition,
  TPosition,
  TImageWithMetadata,
} from '@components/typescript';
import styles from '@components/Slider/Slider.styles';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import Button from '@components/Button/Button';
import { useResponsive } from '@features/responsive';
import { getRegion } from '@utils/misc';
import { TRegion } from '@typescript/index';
import scrollToElementById from '@utils/scrollToElementById';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';

export type PropTypes = {
  topText?: string;
  topTextColor?: string;
  title?: string;
  titleColor?: string;
  description?: string;
  descriptionColor?: string;
  position: TPosition;
  href: string;
  buttonVerticalPosition: TVerticalPosition;
  buttonHorizontalPosition: THorizontalPosition;
  buttonText: string;
  buttonStyle: TCmsButtonStyle;
  textImg?: TImageWithMetadata;
  additionalButton?: ISliderAdditionalButton;
  isFirst?: boolean;
};
const SliderContent = ({
  href,
  title = '',
  description = '',
  position,
  titleColor = '',
  descriptionColor = '',
  topTextColor = '',
  topText = '',
  buttonVerticalPosition,
  buttonHorizontalPosition,
  buttonText,
  buttonStyle,
  textImg = undefined,
  additionalButton = undefined,
  isFirst = false,
}: PropTypes) => {
  const { isMobile, isMobilelg } = useResponsive();
  const [buttonWidthChanged, setButtonWidthChanged] = useState(false);
  const buttonOneRef = useRef<HTMLButtonElement>(null);
  const buttonTwoRef = useRef<HTMLButtonElement>(null);
  const isAdditionalButtonEnabled =
    additionalButton && additionalButton.additionalButtonText && additionalButton.additionalButtonLink.path;
  const region = getRegion('uppercase') as TRegion;

  const isVoxboxLink = (link: string) => {
    if (
      link.includes('voxbox.vox.pl') ||
      link.includes('voxbox.voxmuebles.com') ||
      link.includes('voxbox-global.vox.pl') ||
      link.includes('voxbox.voxfurniture.com')
    )
      return true;
    return false;
  };

  const renderButton = () => {
    if (buttonText && href && !isAdditionalButtonEnabled) {
      return (
        <>
          {region === 'PL' &&
          typeof window !== 'undefined' &&
          typeof window.LiveChatWidget !== 'undefined' &&
          (href === '#livechat' || href === '#livechat-voxbox') ? (
            <div
              sx={styles.buttonLinkPosition(buttonHorizontalPosition, buttonVerticalPosition, false)}
              data-testid="slider-content-button-link-position"
            >
              <Button
                ref={buttonTwoRef}
                cy="SliderContent-SliderButton"
                addClassName="additionalHeaderActionButton"
                size="default"
                variant={
                  buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                    ? 'outlined'
                    : 'default'
                }
                variantColor={buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'}
              >
                <span
                  data-id={href === '#livechat' ? 'VeAR_qiV4Vl' : 'eei5o0iwpA2Z'}
                  className="livechat_button"
                  dangerouslySetInnerHTML={{
                    __html: `<a onclick="event.preventDefault(); if (LiveChatWidget !== undefined) { LiveChatWidget.call('maximize');return false; }" href="https://www.livechat.com/?utm_source=chat_button&utm_medium=referral&utm_campaign=lc_12811512">${buttonText}</a>`,
                  }}
                />
              </Button>
            </div>
          ) : (
            <div sx={styles.buttonLinkPosition(buttonHorizontalPosition, buttonVerticalPosition, isVoxboxLink(href))}>
              {href.slice(0, 1) === '#' ? (
                <Button
                  cy="SliderContent-SliderButton"
                  onClick={() => scrollToElementById(href.substring(1))}
                  size="default"
                  variant={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                      ? 'outlined'
                      : 'default'
                  }
                  variantColor={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'
                  }
                >
                  {buttonText}
                </Button>
              ) : (
                <Button
                  cy="SliderContent-SliderButton"
                  href={href}
                  size="default"
                  variant={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                      ? 'outlined'
                      : 'default'
                  }
                  variantColor={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'
                  }
                >
                  {buttonText}
                </Button>
              )}
            </div>
          )}
        </>
      );
    }
    if (buttonText && href && isAdditionalButtonEnabled && additionalButton.additionalButtonLink.path) {
      return (
        <>
          <div sx={styles.buttonAdditionalLinkPosition(buttonHorizontalPosition, buttonVerticalPosition)}>
            {region === 'PL' &&
            typeof window !== 'undefined' &&
            typeof window.LiveChatWidget !== 'undefined' &&
            (href === '#livechat' || href === '#livechat-voxbox') ? (
              <div sx={styles.additionalButtonConatiner(false)}>
                <Button
                  cy="SliderContent-SliderButton"
                  ref={buttonOneRef}
                  size="default"
                  variant={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                      ? 'outlined'
                      : 'default'
                  }
                  variantColor={
                    buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'
                  }
                  fullWidth={!isMobile && !isMobilelg}
                >
                  <span
                    data-id={href === '#livechat' ? 'VeAR_qiV4Vl' : 'eei5o0iwpA2Z'}
                    className="livechat_button"
                    dangerouslySetInnerHTML={{
                      __html: `<a onclick="event.preventDefault(); if (LiveChatWidget !== undefined) { LiveChatWidget.call('maximize');return false; }" href="https://www.livechat.com/?utm_source=chat_button&utm_medium=referral&utm_campaign=lc_12811512">${buttonText}</a>`,
                    }}
                  />
                </Button>
              </div>
            ) : (
              <div sx={styles.additionalButtonConatiner(isVoxboxLink(href))}>
                {href.slice(0, 1) === '#' ? (
                  <Button
                    cy="SliderContent-SliderButton"
                    onClick={() => scrollToElementById(href.substring(1))}
                    ref={buttonOneRef}
                    size="default"
                    variant={
                      buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                        ? 'outlined'
                        : 'default'
                    }
                    variantColor={
                      buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'
                    }
                    fullWidth={!isMobile && !isMobilelg}
                  >
                    {buttonText}
                  </Button>
                ) : (
                  <Button
                    cy="SliderContent-SliderButton"
                    ref={buttonOneRef}
                    href={href}
                    size="default"
                    variant={
                      buttonStyle === 'outlinedGraphite' || buttonStyle === 'outlinedWhite' || !buttonStyle
                        ? 'outlined'
                        : 'default'
                    }
                    variantColor={
                      buttonStyle === 'outlinedGraphite' || buttonStyle === 'solidGraphite' ? 'dark' : 'white'
                    }
                    fullWidth={!isMobile && !isMobilelg}
                  >
                    {buttonText}
                  </Button>
                )}
              </div>
            )}
            <div sx={styles.additionalText}>
              <span>{additionalButton.additionalText}</span>
            </div>
            {region === 'PL' &&
            typeof window !== 'undefined' &&
            typeof window.LiveChatWidget !== 'undefined' &&
            (additionalButton.additionalButtonLink.path === '#livechat' ||
              additionalButton.additionalButtonLink.path === '#livechat-voxbox') ? (
              <div sx={styles.additionalButtonConatiner(false)}>
                <Button
                  cy="SliderContent-OpenLiveChatButton"
                  ref={buttonTwoRef}
                  addClassName="additionalHeaderActionButton"
                  size="default"
                  variantColor={
                    additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                    additionalButton.additionalButtonStyle === 'solidGraphite'
                      ? 'dark'
                      : 'white'
                  }
                  variant={
                    additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                    additionalButton.additionalButtonStyle === 'outlinedWhite'
                      ? 'outlined'
                      : 'default'
                  }
                  fullWidth={!isMobile && !isMobilelg}
                >
                  <span
                    data-id={href === '#livechat' ? 'VeAR_qiV4Vl' : 'eei5o0iwpA2Z'}
                    className="livechat_button"
                    dangerouslySetInnerHTML={{
                      __html: `<a onclick="event.preventDefault(); if (LiveChatWidget !== undefined) { LiveChatWidget.call('maximize');return false; }" href="https://www.livechat.com/?utm_source=chat_button&utm_medium=referral&utm_campaign=lc_12811512">${additionalButton.additionalButtonText}</a>`,
                    }}
                  />
                </Button>
              </div>
            ) : (
              <div sx={styles.additionalButtonConatiner(isVoxboxLink(additionalButton.additionalButtonLink.path))}>
                {additionalButton.additionalButtonLink.path.slice(0, 1) === '#' ? (
                  <Button
                    cy="SliderContent-OpenLiveChatButton"
                    onClick={() => scrollToElementById(additionalButton.additionalButtonLink.path.substring(1))}
                    ref={buttonTwoRef}
                    addClassName="additionalHeaderActionButton"
                    size="default"
                    variantColor={
                      additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                      additionalButton.additionalButtonStyle === 'solidGraphite'
                        ? 'dark'
                        : 'white'
                    }
                    variant={
                      additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                      additionalButton.additionalButtonStyle === 'outlinedWhite'
                        ? 'outlined'
                        : 'default'
                    }
                    fullWidth={!isMobile && !isMobilelg}
                  >
                    {additionalButton.additionalButtonText}
                  </Button>
                ) : (
                  <Button
                    ref={buttonTwoRef}
                    href={additionalButton.additionalButtonLink.path}
                    addClassName="additionalHeaderActionButton"
                    size="default"
                    cy="SliderContent-AdditionalButton"
                    variantColor={
                      additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                      additionalButton.additionalButtonStyle === 'solidGraphite'
                        ? 'dark'
                        : 'white'
                    }
                    variant={
                      additionalButton.additionalButtonStyle === 'outlinedGraphite' ||
                      additionalButton.additionalButtonStyle === 'outlinedWhite'
                        ? 'outlined'
                        : 'default'
                    }
                    fullWidth={!isMobile && !isMobilelg}
                  >
                    {additionalButton.additionalButtonText}
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      );
    }
    return <></>;
  };

  useEffect(() => {
    if (isAdditionalButtonEnabled && buttonOneRef.current && buttonTwoRef.current && !buttonWidthChanged) {
      const buttonOneWidth = buttonOneRef.current.offsetWidth;
      const buttonTwoWidth = buttonTwoRef.current.offsetWidth;
      const maxWidth = Math.ceil(Math.max(buttonOneWidth, buttonTwoWidth));
      buttonOneRef.current.style.width = `${maxWidth + 2}px`;
      buttonTwoRef.current.style.width = `${maxWidth + 2}px`;
      setButtonWidthChanged(true);
    }
  }, [isAdditionalButtonEnabled, buttonOneRef, buttonTwoRef]);
  return (
    <div sx={styles.sliderContentContainer(position)} data-testid="SliderContent">
      <div sx={styles.linkContainer}>
        {buttonVerticalPosition === 'start' && renderButton()}
        {!!topText && <p sx={styles.sliderTopText(position, topTextColor)}>{topText}</p>}
        {!!title && (
          <Heading
            h={isFirst ? 1 : 2}
            display={3}
            mobile
            align="center"
            color={titleColor || theme.rawColors.neutralWhite}
          >
            {title}
          </Heading>
        )}
        {!!description && (
          <div sx={styles.sliderDescription(position, descriptionColor)}>
            <Heading h={4} headingType={4} color={descriptionColor || theme.rawColors.neutralWhite} mobile>
              {description}
            </Heading>
          </div>
        )}
        {!!textImg && (
          <div sx={styles.textImgContainer}>
            <ResponsiveImage
              image={typeof textImg === 'string' ? textImg : textImg.path}
              width={390}
              height={106}
              priority={isFirst}
              alt={typeof textImg !== 'string' ? textImg.metadata.alt || title || '' : title || ''}
            />
          </div>
        )}
        {buttonVerticalPosition !== 'start' && renderButton()}
      </div>
    </div>
  );
};

export default SliderContent;
